/* Harper & Stone — the dropdown.
 *
 * A <select>'s popup is drawn by the operating system and cannot be styled.
 * That is the "system default" this component exists to replace: a grey
 * rounded sheet in the OS font, in the middle of a page set in Marcellus and
 * Archivo.
 *
 * It does NOT replace the select. The real control stays in the DOM and
 * stays the source of truth — it owns the value, it submits with the form,
 * and it fires the `change` events the variant picker and the sort control
 * already listen for. This element renders a styled listbox next to it and
 * keeps the two in step. With JS off, or if this script fails, the page has
 * a working select and nobody notices.
 *
 * Below 48rem it does nothing at all. A phone's native picker is a wheel at
 * the bottom of the screen, one thumb away, and no web imitation of it is
 * better — so there the stylesheet dresses the closed control and leaves the
 * popup to the platform.
 *
 * Markup:
 *   <hs-select>
 *     <select>…</select>          <- the real control, visually hidden at >=48rem
 *   </hs-select>
 */

const DESKTOP = '(min-width: 48rem)';

class HsSelect extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    if (!this.select) return;

    this.mq = matchMedia(DESKTOP);
    this.onMedia = () => this.#sync();
    this.mq.addEventListener('change', this.onMedia);

    /* The select may be re-rendered under us — the variant picker swaps the
     * whole form on a variant change, and the search sort re-renders with
     * results. Watching it keeps the button's label honest. */
    this.select.addEventListener('change', () => this.#paint());
    /* The variant picker relabels and disables options as availability
     * changes; the button's label and the rows are a copy, so refresh them. */
    this.select.addEventListener('hs:options-changed', () => this.#paint());

    this.#build();
    this.#sync();
  }

  disconnectedCallback() {
    this.mq?.removeEventListener('change', this.onMedia);
    document.removeEventListener('click', this.onOutside, true);
  }

  #build() {
    /* The button the shopper actually sees. `type="button"` matters: inside
     * the product form a bare <button> submits, which would add to cart when
     * someone opened the carat list. */
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.className = 'hsel__button';
    this.button.setAttribute('aria-haspopup', 'listbox');
    this.button.setAttribute('aria-expanded', 'false');

    this.label = document.createElement('span');
    this.label.className = 'hsel__label';
    this.button.append(this.label);

    const chevron = document.createElement('span');
    chevron.className = 'hsel__chevron';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" width="16" height="16">' +
      '<path d="m6 9 6 6 6-6" stroke="currentColor" stroke-width="1.4" ' +
      'stroke-linecap="round" stroke-linejoin="round"/></svg>';
    this.button.append(chevron);

    this.list = document.createElement('div');
    this.list.className = 'hsel__list';
    this.list.setAttribute('role', 'listbox');
    this.list.hidden = true;

    /* The button labels the listbox for a screen reader. The native select
     * already has a <label>, so borrow its text rather than inventing one. */
    const labelled = this.select.labels?.[0]?.textContent?.trim();
    if (labelled) this.list.setAttribute('aria-label', labelled);

    this.append(this.button, this.list);

    /* The native select is hidden in custom mode, so a click on its <label>
     * has nothing to focus. Send it to the button instead. */
    for (const label of this.select.labels ?? []) {
      label.addEventListener('click', (e) => {
        if (this.dataset.mode !== 'custom') return;
        e.preventDefault();
        this.button.focus();
      });
    }

    this.button.addEventListener('click', () => this.#toggle());
    this.button.addEventListener('keydown', (e) => this.#buttonKeys(e));
    this.list.addEventListener('keydown', (e) => this.#listKeys(e));

    /* Capture phase: a click inside another dropdown should close this one,
     * and a listener on the document in bubble phase can be beaten by a
     * handler that stops propagation. */
    this.onOutside = (e) => {
      if (!this.contains(e.target)) this.close();
    };
    document.addEventListener('click', this.onOutside, true);

    this.#paint();
  }

  /* Rebuild the options from the select. Called on every paint because the
   * variant picker disables and re-enables values as the metal changes. */
  #paint() {
    if (!this.list) return;
    const options = [...this.select.options];

    this.label.textContent = this.select.selectedOptions[0]?.textContent.trim() || '';
    /* A placeholder ("Select a size") is an empty value: the label shows it
     * muted, and a `hidden` option is never a row in the list. */
    this.button.toggleAttribute('data-placeholder', this.select.value === '');

    this.list.replaceChildren(
      ...options.flatMap((option, index) => {
        if (option.hidden) return [];
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'hsel__option';
        row.setAttribute('role', 'option');
        row.setAttribute('aria-selected', String(index === this.select.selectedIndex));
        row.textContent = option.textContent.trim();
        row.dataset.index = String(index);
        if (option.disabled) {
          row.disabled = true;
          row.setAttribute('aria-disabled', 'true');
        }
        row.addEventListener('click', () => this.#choose(index));
        return row;
      }),
    );
  }

  /* The select owns the value, so a choice sets it there and lets it tell
   * everyone else. `bubbles` matters — the variant picker listens on the
   * form, not on each control. */
  #choose(index) {
    if (this.select.selectedIndex !== index) {
      this.select.selectedIndex = index;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    this.#paint();
    this.close();
    this.button.focus();
  }

  #toggle() {
    if (this.list.hidden) this.open();
    else this.close();
  }

  open() {
    if (!this.list.hidden) return;
    /* Only one open at a time, or two panels overlap and the second is
     * unreachable behind the first. */
    for (const other of document.querySelectorAll('hs-select')) {
      if (other !== this) other.close?.();
    }
    this.#paint();
    this.list.hidden = false;
    this.button.setAttribute('aria-expanded', 'true');
    this.dataset.open = 'true';

    /* Open upward when there is not room below. A panel that runs off the
     * bottom of the screen is a panel whose last option cannot be reached. */
    const room = window.innerHeight - this.button.getBoundingClientRect().bottom;
    this.dataset.drop = room < Math.min(280, this.list.scrollHeight + 16) ? 'up' : 'down';

    this.list.querySelector('[aria-selected="true"]')?.focus();
  }

  close() {
    if (!this.list || this.list.hidden) return;
    this.list.hidden = true;
    this.button.setAttribute('aria-expanded', 'false');
    delete this.dataset.open;
  }

  #buttonKeys(event) {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      this.open();
    }
  }

  /* Arrow keys move between options, Escape closes, Home and End jump. This
   * is the behaviour the native control gives away for free and the one
   * thing a div-based dropdown usually forgets. */
  #listKeys(event) {
    const rows = [...this.list.querySelectorAll('.hsel__option:not([disabled])')];
    const here = rows.indexOf(document.activeElement);

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        this.button.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        rows[Math.min(here + 1, rows.length - 1)]?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (here <= 0) {
          this.close();
          this.button.focus();
        } else rows[here - 1]?.focus();
        break;
      case 'Home':
        event.preventDefault();
        rows[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        rows[rows.length - 1]?.focus();
        break;
      case 'Tab':
        this.close();
        break;
      default:
        break;
    }
  }

  /* Above 48rem the custom listbox is in charge and the native select is
   * hidden from sight but kept in the form. Below it the native control is
   * the whole story. The stylesheet does the hiding; this only has to keep
   * the button's label right and make sure nothing is left open. */
  #sync() {
    const desktop = this.mq.matches;
    this.dataset.mode = desktop ? 'custom' : 'native';
    if (!desktop) this.close();
    else this.#paint();
  }
}

if (!customElements.get('hs-select')) customElements.define('hs-select', HsSelect);
